import { Box, Grid, makeStyles, Theme } from "@material-ui/core";
import { GeoAutoCompletion } from "components/GeoAutoCompletion";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { InationalTransportPoint, isSameItem, ItemAtPoint, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { Address, EActionType } from "generated/graphql";
import React, { useCallback, useContext, useEffect } from "react";
import { Button } from "ui/Button";
import ArticleToPickAtPoint from "../shared/ArticleToPickAtPoint";
import { TitleButton } from "../shared/Catalog/CatalogStyles";
import DestinationPoint from "../shared/DestinationPoint";
import { comparer, isFieldWithValue } from "../shared/utils";

const useStyles = makeStyles((theme: Theme) => ({
  gridContainer: {
    padding: "2rem 1rem 0rem",
    display: "flex",
    justifyContent: "center",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  Box: {
    border: "1px solid #399ebf",
    height: "auto",
    width: "70%",
    padding: "20px",
    borderRadius: "5px",
    [theme.breakpoints.down("xs")]: { width: "100%", padding: "28px 6px 17px 12px" },
  },
  btn: {
    fontSize: "18px",
    [theme.breakpoints.down("xs")]: { marginLeft: -60, fontSize: "12px" },
  },
  btnAddStop: {
    backgroundColor: "rgba(64, 180, 229, 0.15)",
    [theme.breakpoints.down("xs")]: {
      height: "4vh",
      minWidth: "auto",
      right: "25px"
    }
  }
}));



export default function Shipment() {
  const { requestQuoteNT, fieldError, isEditedNT, setFieldError, truckType, setRequestQuoteNT } =
  useContext(NewRequestQuoteContext);
  const { text } = useText();
  const dispatchAlert = useAlertDispatch();
  const styles = useStyles();


  const removeItemFromToDeliver = (stop: InationalTransportPoint, items: ItemAtPoint[]) => {
    const newStopValue: ItemAtPoint[] = [];
    stop.itemsToDelivered.map((it) => {
      if (!items.filter((i) => i?.product._id === it.product._id && i?.product?.pickup === it.product.pickup).length)
        newStopValue.push(it);
    });
    return newStopValue;
  };

  /**
   *Cette fonction permet de supprimer les produits supprimés dans un point Pickup au niveau des points de delivery
   *ayant déjà ce produit.
   */
  const onItemsRemovedFromPoint = (stops: InationalTransportPoint[], removedItems: ItemAtPoint[]) => {
    const newValueStops: InationalTransportPoint[] = [];
    stops?.map((stop) => {
      if (stop?.itemsToDelivered?.length > 0) {
        newValueStops.push({
          ...stop,
          itemsToDelivered: removeItemFromToDeliver(stop, removedItems),
        });
      } else {
        newValueStops.push(stop);
      }
    });
    return newValueStops;
  };

  const handleSetArticlesToPick = (articlesTopick: ItemAtPoint[]) => {
    const differenceItems = requestQuoteNT?.stops[0]?.itemsToPick?.filter(comparer(articlesTopick));
    const point: InationalTransportPoint = {
      itemsToPick: articlesTopick,
      address: requestQuoteNT?.stops[0]?.address,
      action: EActionType.Pickup,
    };
    const stops = [...requestQuoteNT?.stops];
    stops[0] = { ...point };

    if (differenceItems?.length > 0) {
      setRequestQuoteNT({ ...requestQuoteNT, stops: onItemsRemovedFromPoint(stops, differenceItems) });
    } else {
      setRequestQuoteNT({ ...requestQuoteNT, stops });
    }
  };

  const handleSetPoint = React.useCallback((point: InationalTransportPoint, positionPoint: number) => {
    if (requestQuoteNT?.stops) {
      const differenceItems = requestQuoteNT?.stops[positionPoint]?.itemsToPick?.filter(comparer(point?.itemsToPick));
      const stops = requestQuoteNT?.stops;
      stops[positionPoint] = point;
      if (stops[positionPoint] && stops[positionPoint]?.action === EActionType.Pickup && differenceItems?.length > 0) {
        setRequestQuoteNT({ ...requestQuoteNT, stops: onItemsRemovedFromPoint(stops, differenceItems) });
      } else {
        setRequestQuoteNT({ ...requestQuoteNT, stops });
      }
    } else {
      return dispatchAlert({
        message: text.catalog.error_define_first_point,
        severity: "warning",
      });
    }
  }, [requestQuoteNT?.stops]);

  /**
   *
   * @param oldPoint L'ancienne valeur du point
   * @param newPoint La nouvelle valeur du point
   * @returns un nouveau Point: contenant les bonnes [count] pour chaque produit
   */
  const newValueAtPoint = (oldPoint: InationalTransportPoint, newPoint: InationalTransportPoint) => {
    const newValues: ItemAtPoint[] = [];
    newPoint?.itemsToDelivered?.map(async (item) => {
      const res = oldPoint?.itemsToDelivered?.filter(
        (oldItem) => oldItem?.product?._id === item?.product?._id && oldItem?.product?.pickup === item?.product?.pickup
      )[0];
      if (res) {
        newValues.push({
          product: item?.product,
          pickup: item?.pickup,
          counter: item?.counter - res?.counter,
        });
      } else {
        newValues.push({
          product: item?.product,
          pickup: item?.pickup,
          counter: item?.counter,
        });
      }
    });
    return newValues;
  };

  const redifineObject = (
    newPoint: InationalTransportPoint,
    positionPoint: number,
    newProductCount: ItemAtPoint[],
    differenceItems: ItemAtPoint[],
    removeAfterThread?: boolean,
    updatedStops?: InationalTransportPoint[]
  ) => {
    try {
      const points = removeAfterThread ? updatedStops || [] : requestQuoteNT?.stops;
      points[positionPoint] = newPoint;
      newProductCount?.map(async (item) => {
        for (let i = 0; i < points[item?.product?.pickup].itemsToPick?.length; i++) {
          if (
            isSameItem(points[item?.product?.pickup].itemsToPick[i].product, item?.product) &&
            points[item?.product?.pickup].itemsToPick[i].product?.pickup === item?.product?.pickup
          ) {
            points[item?.product?.pickup].itemsToPick[i].remainder -= item?.counter;
          }
        }
      });
      if (differenceItems?.length) {
        differenceItems?.map(async (item) => {
          for (let i = 0; i < points[item?.product?.pickup].itemsToPick?.length; i++) {
            if (isSameItem(points[item?.product?.pickup].itemsToPick[i].product, item?.product)) {
              points[item?.product?.pickup].itemsToPick[i].remainder += item?.counter;
            }
          }
        });
      }
      if (removeAfterThread) points?.splice(positionPoint, 1);
      setRequestQuoteNT({ ...requestQuoteNT, stops: points });
    } catch (error) { }
  };

  const handleSetPointDelivery = (point: InationalTransportPoint, positionPoint: number) => {
    if (requestQuoteNT?.stops) {
      const differenceItems = requestQuoteNT?.stops[positionPoint]?.itemsToDelivered?.filter(
        comparer(point?.itemsToDelivered)
      );
      const newProductCount = newValueAtPoint(requestQuoteNT?.stops[positionPoint], point);
      redifineObject(point, positionPoint, newProductCount, differenceItems);
    } else {
      return dispatchAlert({
        message: text.catalog.error_define_first_point,
        severity: "warning",
      });
    }
  };

  const handleDepartureAddress = (values: Address) => {
    const point: InationalTransportPoint = {
      itemsToPick: requestQuoteNT?.stops[0]?.itemsToPick,
      address: values,
      action: EActionType.Pickup,
    };
    const stops = [...requestQuoteNT?.stops] || [];
    stops[0] = point;
    stops[1] = { action: stops[1]?.action || null, address: stops[1]?.address || null };
    setRequestQuoteNT({ ...requestQuoteNT, stops });
  };


  const getPossiblePointToPick = (index: number) => {
    const arrayOfPickupPoint: InationalTransportPoint[] = [];
    for (let i = 0; i < index; i++) {
      if (
        requestQuoteNT?.stops[i]?.action === EActionType.Pickup ||
        requestQuoteNT?.stops[i]?.action === EActionType.PickupDelivery
      )
        arrayOfPickupPoint.push(requestQuoteNT?.stops[i]);
    }
    return arrayOfPickupPoint;
  };

  const handleDeletePoint = async (positionPoint: number) => {
    if (requestQuoteNT?.stops) {
      let stops = requestQuoteNT?.stops;
      const itemsPicked = stops[positionPoint]?.itemsToPick || [];
      const itemsDelivered = stops[positionPoint]?.itemsToDelivered || [];
      if (!itemsPicked?.length && !itemsDelivered?.length) {
        stops?.splice(positionPoint, 1);
        setRequestQuoteNT({ ...requestQuoteNT, stops });
      } else {
        if (itemsPicked?.length) {
          stops = onItemsRemovedFromPoint(stops, itemsPicked);
        }
        if (itemsDelivered?.length) {
          redifineObject(stops[positionPoint], positionPoint, [], itemsDelivered, true, stops);
        } else {
          stops?.splice(positionPoint, 1);
          setRequestQuoteNT({ ...requestQuoteNT, stops });
        }
      }
    }
  };

  const renderPoints = useCallback(
    () =>
      requestQuoteNT?.stops?.length > 1 ? (
        requestQuoteNT?.stops
          ?.slice(1)
          ?.map?.((point, index) => (
            <DestinationPoint
              point={JSON.parse(JSON.stringify(point))}
              possiblePointsTopick={getPossiblePointToPick(index + 1)}
              positionPoint={index + 1}
              onChangePoint={handleSetPoint}
              onChangePointDelivery={handleSetPointDelivery}
              key={index}
              onDeletePoint={handleDeletePoint}
            />
          ))
      ) : (
        <DestinationPoint
          point={requestQuoteNT?.stops[1]}
          possiblePointsTopick={getPossiblePointToPick(1)}
          positionPoint={1}
          onChangePoint={handleSetPoint}
          onChangePointDelivery={handleSetPointDelivery}
          key={0}
          onDeletePoint={handleDeletePoint}
        />
      ),
    [requestQuoteNT?.stops]
  );

  const addNewPoint = () => {
    if (requestQuoteNT?.stops?.length >= 2) {
      const newPoint: InationalTransportPoint = null;
      handleSetPoint(newPoint, requestQuoteNT?.stops?.length);
    } else {
      return dispatchAlert({
        message: text.requestQuote.complete_point_error,
        severity: "warning",
      });
    }
  };
  const checkPlaceId = requestQuoteNT?.stops[0]?.address?.place_id === null || undefined;
  const isFull = requestQuoteNT?.stops[0]?.address &&  requestQuoteNT?.stops[0]?.itemsToPick === undefined || requestQuoteNT?.stops[1]?.address === null;

  useEffect(()=>{
    setFieldError(isFull);

  }, [fieldError, requestQuoteNT?.stops[0]?.address,requestQuoteNT?.stops[0]?.itemsToPick, requestQuoteNT?.stops[1]?.address ])

  return (
        <Grid container spacing={5} className={styles.gridContainer}>
          <Box className={styles.Box}>
            <Grid item xs={12}>
              <GeoAutoCompletion
                customLabel={text.all.pickup_location}
                placeholder={text.all.where_to_pick}
                address={requestQuoteNT?.stops[0]?.address}
                onChange={handleDepartureAddress}
                error={fieldError && checkPlaceId}
                helperText={
                  fieldError &&  checkPlaceId
                    ? text.all.this_field_is_required
                    : ""
                }
                required
                locationIcon
              />
            </Grid>

            <Grid item xs={12}>
              <ArticleToPickAtPoint
              addressForPick={requestQuoteNT?.stops[0]?.address?.address}
                articlesTopick={
                  requestQuoteNT?.stops[0]?.itemsToPick
                    ? JSON.parse(JSON.stringify(requestQuoteNT?.stops[0]?.itemsToPick)) // For Passing value without ref
                    : requestQuoteNT?.stops[0]?.itemsToPick
                }
                onChange={handleSetArticlesToPick}
                positionPoint={0}
              />
            </Grid>
          </Box>
          {renderPoints()}
          <Grid item xs={9} className={styles.btn}>
            <Button color="primary" variant="outlined" onClick={addNewPoint} className={styles.btnAddStop}>
              +
            </Button>
            <TitleButton>{text.requestQuote.add_destination_point}</TitleButton>
          </Grid>
        </Grid>
  );
}
